import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Copy, Pencil, Plus, Search, Shield, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import {
  ENTRIES,
  ENTRY_GROUPS,
  FULL_RIGHTS,
  levelOf,
  useGuildRights,
  type EntryKey,
  type GrantedLevel,
  type Level,
  type Rights,
} from '@/lib/access';
import type { AccessOverview, DashboardActivity, GuildAccessData } from '@/lib/types';
import { useGuilds, useMe } from '@/lib/hooks';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { UserMention } from '@/components/UserMention';
import { AdminBackLink } from './AdminPanel';
import { BackLink } from './config/_shared';

/**
 * Zugriff (access model 2026-10), organised by server like the Admin-Panel:
 *   - AccessPage (/access, admins): admins, one card per server (people +
 *     templates), people overview
 *   - ServerAccessPage (/server/:guildId/access): the same card for one
 *     server — reachable for managers with "Zugriff verwalten"
 * The API (routes/access.ts) enforces the manager guard rules; the UI mirrors
 * them via the `manageable` flags and by capping the rights editor at the
 * manager's own rights.
 */

const LEVEL_LABEL: Record<Level, string> = { none: 'kein Zugriff', view: 'ansehen', edit: 'bearbeiten' };
const RANK: Record<Level, number> = { none: 0, view: 1, edit: 2 };
const OWN = '__own__';

interface Member {
  id: string;
  displayName: string;
  rawUsername: string;
  avatarUrl: string | null;
  bot: boolean;
}

// ------------------------------------------------------------------ pages

export function AccessPage() {
  const overview = useQuery({
    queryKey: ['access-overview'],
    queryFn: () => api<AccessOverview>('/api/access/overview'),
  });

  if (overview.isLoading) return <p className="text-muted-foreground">Lade Zugriff…</p>;
  if (overview.error) return <p className="text-destructive">Fehler: {(overview.error as Error).message}</p>;
  if (!overview.data) return null;
  const { admins, guilds, people } = overview.data;
  const guildName = new Map(guilds.map((g) => [g.id, g.name]));

  return (
    <div className="mx-auto max-w-4xl">
      <AdminBackLink />
      <h1 className="mb-6 text-2xl font-semibold">Zugriff</h1>

      <AdminsCard admins={admins} />

      <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Server
      </h2>
      <div className="space-y-4">
        {guilds.map((g) => (
          <GuildAccessCard
            key={g.id}
            guildId={g.id}
            name={g.name}
            iconUrl={g.iconUrl}
            summary={`${g.personCount} ${g.personCount === 1 ? 'Person' : 'Personen'}`}
            copySources={guilds.filter((x) => x.id !== g.id)}
          />
        ))}
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-base">Personen-Übersicht</CardTitle>
          <CardDescription>Wer ist auf welchem Server mit welcher Vorlage.</CardDescription>
        </CardHeader>
        <CardContent>
          {people.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch niemand zugeordnet.</p>
          ) : (
            <ul className="divide-y">
              {people.map((p) => (
                <li key={p.discordId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                  <span className="min-w-[140px] flex-1">
                    <UserMention id={p.discordId} />
                  </span>
                  {p.assignments.map((a) => (
                    <span key={a.guildId} className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                      {guildName.get(a.guildId) ?? a.guildId} · {a.templateName ?? 'Eigene Rechte'}
                    </span>
                  ))}
                  <LoginInfo discordId={p.discordId} activity={p.activity} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function ServerAccessPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { isAdmin } = useGuildRights(guildId);
  const me = useMe();
  const guilds = useGuilds(true);
  if (!guildId) return null;
  // Copy sources: other servers where this user may at least view Zugriff.
  const copySources = (guilds.data?.guilds ?? [])
    .filter((g) => g.id !== guildId && (isAdmin || me.data?.rights?.[g.id]?.access))
    .map((g) => ({ id: g.id, name: g.name }));
  return (
    <div className="mx-auto max-w-4xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">Zugriff</h1>
      <GuildAccessCard guildId={guildId} defaultOpen copySources={copySources} />
    </div>
  );
}

// ------------------------------------------------------------- login info

const RELATIVE = new Intl.RelativeTimeFormat('de', { numeric: 'auto' });

function ago(iso: string | null): string {
  if (!iso) return 'nie';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 10) return 'gerade eben';
  if (mins < 60) return RELATIVE.format(-mins, 'minute');
  if (mins < 24 * 60) return RELATIVE.format(-Math.round(mins / 60), 'hour');
  return RELATIVE.format(-Math.round(mins / (24 * 60)), 'day');
}

/** "Zuletzt online · Logins · Besuche" + expandable 90-day history (admins only). */
function LoginInfo({ discordId, activity }: { discordId: string; activity: DashboardActivity }) {
  const [open, setOpen] = useState(false);
  const history = useQuery({
    queryKey: ['access-activity', discordId],
    queryFn: () =>
      api<{ logins: string[]; visitDays: string[] }>(`/api/access/people/${discordId}/activity`),
    enabled: open,
  });
  return (
    <div className="basis-full text-xs text-muted-foreground">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded hover:text-foreground"
        title={activity.lastSeenAt ? new Date(activity.lastSeenAt).toLocaleString('de-DE') : undefined}
      >
        Zuletzt online {ago(activity.lastSeenAt)} · {activity.loginCount}{' '}
        {activity.loginCount === 1 ? 'Login' : 'Logins'} · {activity.visits30}{' '}
        {activity.visits30 === 1 ? 'Besuch' : 'Besuche'} (30 Tage)
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-md border p-2">
          {history.isLoading && <p>Lade Verlauf…</p>}
          {history.data && (
            <>
              <div>
                <div className="mb-1 font-medium text-foreground">Logins</div>
                {history.data.logins.length === 0 ? (
                  <p>Keine in den letzten 90 Tagen.</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {history.data.logins.map((at) => (
                      <span key={at} className="rounded bg-muted px-1.5 py-0.5">
                        {new Date(at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <div className="mb-1 font-medium text-foreground">Besuchstage</div>
                {history.data.visitDays.length === 0 ? (
                  <p>Keine in den letzten 90 Tagen.</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {history.data.visitDays.map((d) => (
                      <span key={d} className="rounded bg-muted px-1.5 py-0.5">
                        {new Date(`${d}T12:00:00Z`).toLocaleDateString('de-DE', { dateStyle: 'short' })}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <p>Verlauf der letzten 90 Tage. Logins werden seit 22.10.2026 gezählt.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------- admins

function AdminsCard({ admins }: { admins: AccessOverview['admins'] }) {
  const qc = useQueryClient();
  const me = useMe();
  const [discordId, setDiscordId] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['access-overview'] });
  const add = useMutation({
    mutationFn: () => api('/api/access/admins', { method: 'POST', body: JSON.stringify({ discordId: discordId.trim() }) }),
    onSuccess: () => {
      setDiscordId('');
      invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/access/admins/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Shield className="h-4 w-4 text-primary" /> Admins
        </CardTitle>
        <CardDescription>Dürfen auf jedem Server alles, ohne Zuordnung.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="divide-y">
          {admins.map((a) => (
            <li key={a.discordId} className="flex flex-wrap items-center gap-2 py-2 text-sm">
              <span className="flex-1">
                <UserMention id={a.discordId} />
              </span>
              {a.isBootstrap ? (
                <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground">Bootstrap-Admin</span>
              ) : a.discordId !== me.data?.user.discordId ? (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm('Admin-Rechte entfernen? Ohne Server-Zuordnung kann sich die Person danach nicht mehr einloggen.')) {
                      remove.mutate(a.discordId);
                    }
                  }}
                >
                  Admin entfernen
                </Button>
              ) : null}
              <LoginInfo discordId={a.discordId} activity={a.activity} />
            </li>
          ))}
        </ul>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (/^\d{15,22}$/.test(discordId.trim())) add.mutate();
          }}
        >
          <Input
            value={discordId}
            onChange={(e) => setDiscordId(e.target.value)}
            placeholder="Discord-ID"
            className="max-w-xs"
          />
          <Button type="submit" variant="outline" disabled={!/^\d{15,22}$/.test(discordId.trim()) || add.isPending}>
            <Plus className="mr-1 h-4 w-4" /> Admin hinzufügen
          </Button>
          {discordId.trim() && /^\d{15,22}$/.test(discordId.trim()) && <UserMention id={discordId.trim()} />}
        </form>
        <ErrorLine error={add.error || remove.error} />
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------- server card

function GuildAccessCard({
  guildId,
  name,
  iconUrl,
  summary,
  defaultOpen = false,
  copySources,
}: {
  guildId: string;
  name?: string;
  iconUrl?: string | null;
  summary?: string;
  defaultOpen?: boolean;
  copySources: { id: string; name: string }[];
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card>
      <CardHeader className="pb-3">
        <button type="button" className="flex w-full items-center gap-3 text-left" onClick={() => setOpen((o) => !o)}>
          {iconUrl ? (
            <img src={iconUrl} alt="" className="h-9 w-9 rounded-full" />
          ) : name ? (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
              {name.split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase()}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <CardTitle className="truncate text-base">{name ?? 'Personen und Vorlagen'}</CardTitle>
            {summary && <CardDescription>{summary}</CardDescription>}
          </div>
          <ChevronDown className={cn('h-4 w-4 transition-transform', !open && '-rotate-90')} />
        </button>
      </CardHeader>
      {open && <GuildAccessBody guildId={guildId} copySources={copySources} />}
    </Card>
  );
}

function GuildAccessBody({ guildId, copySources }: { guildId: string; copySources: { id: string; name: string }[] }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['access-guild', guildId],
    queryFn: () => api<GuildAccessData>(`/api/access/guilds/${guildId}`),
  });
  const membersQuery = useQuery({
    queryKey: ['members', guildId],
    queryFn: () => api<{ members: Member[] }>(`/api/guilds/${guildId}/members`),
    enabled: !!query.data?.me.canEdit,
    staleTime: 60_000,
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['access-guild', guildId] });
    qc.invalidateQueries({ queryKey: ['access-overview'] });
  };

  if (query.isLoading) return <CardContent className="text-sm text-muted-foreground">Lade…</CardContent>;
  if (query.error) return <CardContent className="text-sm text-destructive">{(query.error as Error).message}</CardContent>;
  if (!query.data) return null;
  const data = query.data;
  // Managers can't hand out more than they have; admins are uncapped.
  const cap: Rights = data.me.isAdmin ? FULL_RIGHTS : data.me.rights;
  const memberIds = membersQuery.data ? new Set(membersQuery.data.members.map((m) => m.id)) : null;

  return (
    <CardContent className="space-y-6">
      <section>
        <SectionTitle>Personen</SectionTitle>
        {data.people.length === 0 && <p className="text-sm text-muted-foreground">Noch niemand zugeordnet.</p>}
        <ul className="divide-y">
          {data.people.map((p) => (
            <PersonRow
              key={p.discordId}
              guildId={guildId}
              person={p}
              templates={data.templates}
              cap={cap}
              isAdmin={data.me.isAdmin}
              notMember={!!memberIds && !memberIds.has(p.discordId)}
              onChanged={invalidate}
            />
          ))}
        </ul>
        {data.me.canEdit && (
          <AddPerson
            guildId={guildId}
            templates={data.templates}
            members={membersQuery.data?.members ?? []}
            existing={new Set(data.people.map((p) => p.discordId))}
            onAdded={invalidate}
          />
        )}
      </section>

      <section>
        <div className="mb-2 flex items-center gap-2">
          <SectionTitle className="mb-0 flex-1">Vorlagen</SectionTitle>
          {data.me.canEdit && copySources.length > 0 && (
            <CopyTemplates guildId={guildId} sources={copySources} onCopied={invalidate} />
          )}
        </div>
        <TemplatesSection guildId={guildId} data={data} cap={cap} onChanged={invalidate} />
      </section>
    </CardContent>
  );
}

// ---------------------------------------------------------------- people

function PersonRow({
  guildId,
  person,
  templates,
  cap,
  isAdmin,
  notMember,
  onChanged,
}: {
  guildId: string;
  person: GuildAccessData['people'][number];
  templates: GuildAccessData['templates'];
  cap: Rights;
  isAdmin: boolean;
  notMember: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Rights>(person.rights as Rights);
  const save = useMutation({
    mutationFn: (body: { templateId?: string | null; rights?: Rights }) =>
      api(`/api/access/guilds/${guildId}/people/${person.discordId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      setEditing(false);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`/api/access/guilds/${guildId}/people/${person.discordId}`, { method: 'DELETE' }),
    onSuccess: onChanged,
  });

  return (
    <li className="py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-[160px] flex-1">
          <UserMention id={person.discordId} />
          {person.isManager && (
            <span className="ml-2 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-primary">Verwalter</span>
          )}
          {person.isSelf && <span className="ml-2 text-xs text-muted-foreground">(du)</span>}
          {notMember && <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">· nicht Mitglied auf diesem Server</span>}
        </span>
        <select
          className="h-9 min-w-[170px] rounded-md border border-input bg-background px-2 text-sm"
          value={person.templateId ?? OWN}
          disabled={!person.manageable || save.isPending}
          onChange={(e) => {
            const value = e.target.value;
            if (value === OWN) {
              // Start own rights from what the template granted (decision 6).
              setDraft(person.rights as Rights);
              setEditing(true);
            } else save.mutate({ templateId: value });
          }}
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id} disabled={!t.manageable && t.id !== person.templateId}>
              {t.name}
            </option>
          ))}
          <option value={OWN}>Eigene Rechte</option>
        </select>
        {person.manageable && person.templateId === null && (
          <Button variant="ghost" size="icon" title="Eigene Rechte bearbeiten" onClick={() => setEditing((e) => !e)}>
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {person.manageable && (
          <Button
            variant="ghost"
            size="icon"
            title="Vom Server entfernen"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm('Vom Server entfernen? Ohne weitere Zuordnung kann sich die Person nicht mehr einloggen.')) remove.mutate();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {editing && (
        <div className="mt-3 rounded-md border bg-muted/30 p-3">
          <RightsEditor value={draft} onChange={setDraft} cap={cap} isAdmin={isAdmin} />
          <div className="mt-3 flex gap-2">
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate({ templateId: null, rights: draft })}>
              {save.isPending ? 'Speichere…' : 'Eigene Rechte speichern'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}
      <ErrorLine error={save.error || remove.error} />
    </li>
  );
}

function AddPerson({
  guildId,
  templates,
  members,
  existing,
  onAdded,
}: {
  guildId: string;
  templates: GuildAccessData['templates'];
  members: Member[];
  existing: Set<string>;
  onAdded: () => void;
}) {
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(null);
  const assignable = templates.filter((t) => t.manageable);
  const [templateId, setTemplateId] = useState<string>('');
  const chosenTemplate = templateId || assignable[0]?.id || '';

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || picked) return [];
    return members
      .filter((m) => !m.bot && !existing.has(m.id))
      .filter((m) => m.displayName.toLowerCase().includes(q) || m.rawUsername.toLowerCase().includes(q) || m.id === q)
      .slice(0, 8);
  }, [query, picked, members, existing]);

  // Fallback (decision 10): a raw Discord ID for someone not on the server.
  const rawId = /^\d{15,22}$/.test(query.trim()) ? query.trim() : null;
  const target = picked?.id ?? rawId;

  const add = useMutation({
    mutationFn: () =>
      api(`/api/access/guilds/${guildId}/people`, {
        method: 'POST',
        body: JSON.stringify({ discordId: target, templateId: chosenTemplate }),
      }),
    onSuccess: () => {
      setQuery('');
      setPicked(null);
      onAdded();
    },
  });

  return (
    <div className="mt-3 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            value={picked ? picked.label : query}
            onChange={(e) => {
              setPicked(null);
              setQuery(e.target.value);
            }}
            placeholder="Mitglied suchen oder Discord-ID"
          />
          {matches.length > 0 && (
            <ul className="glass-float absolute z-10 mt-1 w-full rounded-md border bg-card p-1 shadow-md">
              {matches.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                    onClick={() => setPicked({ id: m.id, label: m.displayName })}
                  >
                    {m.avatarUrl ? <img src={m.avatarUrl} alt="" className="h-5 w-5 rounded-full" /> : <span className="h-5 w-5 rounded-full bg-muted" />}
                    {m.displayName}
                    <span className="text-xs text-muted-foreground">@{m.rawUsername}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <select
          className="h-10 rounded-md border border-input bg-background px-2 text-sm"
          value={chosenTemplate}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          {assignable.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <Button disabled={!target || !chosenTemplate || add.isPending} onClick={() => add.mutate()}>
          <Plus className="mr-1 h-4 w-4" /> Person hinzufügen
        </Button>
      </div>
      {rawId && !picked && !members.some((m) => m.id === rawId) && members.length > 0 && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          <UserMention id={rawId} /> ist nicht Mitglied auf diesem Server.
        </p>
      )}
      {assignable.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">Lege zuerst eine Vorlage an, die du vergeben darfst.</p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Startet mit einer Vorlage — für eigene Rechte danach in der Liste auf „Eigene Rechte" wechseln.
      </p>
      <ErrorLine error={add.error} />
    </div>
  );
}

// ------------------------------------------------------------- templates

function TemplatesSection({
  guildId,
  data,
  cap,
  onChanged,
}: {
  guildId: string;
  data: GuildAccessData;
  cap: Rights;
  onChanged: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {data.templates.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setOpenId(openId === t.id ? null : t.id)}
            className={cn(
              'flex items-center gap-2 rounded-md border px-3 py-2 text-left text-sm hover:bg-accent',
              openId === t.id && 'border-primary'
            )}
          >
            <span className="flex-1 truncate">{t.name}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {t.assignedCount} {t.assignedCount === 1 ? 'Person' : 'Personen'}
            </span>
          </button>
        ))}
        {data.me.canEdit && (
          <button
            type="button"
            onClick={() => {
              setCreating(true);
              setOpenId(null);
            }}
            className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground hover:bg-accent"
          >
            <Plus className="h-4 w-4" /> Neue Vorlage
          </button>
        )}
      </div>
      {creating && (
        <TemplateEditor
          guildId={guildId}
          template={null}
          people={data.people}
          cap={cap}
          isAdmin={data.me.isAdmin}
          canEdit={data.me.canEdit}
          onClose={() => setCreating(false)}
          onChanged={onChanged}
        />
      )}
      {openId &&
        (() => {
          const t = data.templates.find((x) => x.id === openId);
          return t ? (
            <TemplateEditor
              key={t.id}
              guildId={guildId}
              template={t}
              people={data.people}
              cap={cap}
              isAdmin={data.me.isAdmin}
              canEdit={t.manageable}
              onClose={() => setOpenId(null)}
              onChanged={onChanged}
            />
          ) : null;
        })()}
    </div>
  );
}

function TemplateEditor({
  guildId,
  template,
  people,
  cap,
  isAdmin,
  canEdit,
  onClose,
  onChanged,
}: {
  guildId: string;
  template: GuildAccessData['templates'][number] | null;
  people: GuildAccessData['people'];
  cap: Rights;
  isAdmin: boolean;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState(template?.name ?? '');
  const [rights, setRights] = useState<Rights>((template?.rights as Rights) ?? {});
  const base = `/api/access/guilds/${guildId}/templates`;
  const save = useMutation({
    mutationFn: () =>
      template
        ? api(`${base}/${template.id}`, { method: 'PATCH', body: JSON.stringify({ name: name.trim(), rights }) })
        : api(base, { method: 'POST', body: JSON.stringify({ name: name.trim(), rights }) }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api(`${base}/${template!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      onChanged();
      onClose();
    },
  });
  const assignees = template ? people.filter((p) => p.templateId === template.id) : [];

  return (
    <div className="rounded-md border p-3">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name der Vorlage"
          className="max-w-xs"
          disabled={!canEdit}
          maxLength={40}
        />
        <span className="flex-1" />
        {template && canEdit && (
          <Button
            variant="ghost"
            size="sm"
            disabled={remove.isPending}
            onClick={() => {
              const who = assignees.map((p) => p.displayName ?? p.discordId).join(', ');
              const msg = assignees.length
                ? `Vorlage löschen? ${assignees.length} ${assignees.length === 1 ? 'Person behält ihre' : 'Personen behalten ihre'} Rechte als „Eigene Rechte": ${who}`
                : 'Vorlage löschen?';
              if (confirm(msg)) remove.mutate();
            }}
          >
            <Trash2 className="mr-1 h-4 w-4" /> Vorlage löschen
          </Button>
        )}
      </div>
      {!canEdit && template && (
        <p className="mb-2 text-xs text-muted-foreground">Diese Vorlage erlaubt mehr als du selbst — nur ein Admin kann sie ändern.</p>
      )}
      <RightsEditor value={rights} onChange={setRights} cap={cap} isAdmin={isAdmin} disabled={!canEdit} />
      {template && assignees.length > 0 && canEdit && (
        <p className="mt-2 text-xs text-muted-foreground">
          Verknüpft: Änderungen gelten sofort für {assignees.length} {assignees.length === 1 ? 'Person' : 'Personen'}.
        </p>
      )}
      <div className="mt-3 flex gap-2">
        {canEdit && (
          <Button size="sm" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Speichere…' : template ? 'Vorlage speichern' : 'Vorlage anlegen'}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onClose}>
          {canEdit ? 'Abbrechen' : 'Schließen'}
        </Button>
      </div>
      <ErrorLine error={save.error || remove.error} />
    </div>
  );
}

function CopyTemplates({
  guildId,
  sources,
  onCopied,
}: {
  guildId: string;
  sources: { id: string; name: string }[];
  onCopied: () => void;
}) {
  const [from, setFrom] = useState(sources[0]?.id ?? '');
  const [result, setResult] = useState<string | null>(null);
  const copy = useMutation({
    mutationFn: () =>
      api<{ copied: number; skipped: number }>(`/api/access/guilds/${guildId}/templates/copy`, {
        method: 'POST',
        body: JSON.stringify({ fromGuildId: from }),
      }),
    onSuccess: (r) => {
      setResult(`${r.copied} übernommen${r.skipped ? `, ${r.skipped} übersprungen (mehr Rechte als du hast)` : ''}`);
      onCopied();
    },
  });
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <select
        className="h-8 rounded-md border border-input bg-background px-2 text-xs"
        value={from}
        onChange={(e) => setFrom(e.target.value)}
      >
        {sources.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
      <Button variant="outline" size="sm" className="h-8 max-md:h-auto max-md:whitespace-normal max-md:py-1.5" disabled={!from || copy.isPending} onClick={() => copy.mutate()}>
        <Copy className="mr-1 h-3.5 w-3.5" /> Von anderem Server übernehmen
      </Button>
      {result && <span className="text-muted-foreground">{result}</span>}
      <ErrorLine error={copy.error} />
    </div>
  );
}

// ---------------------------------------------------------- rights editor

/**
 * The 32 entries in the 7 sidebar groups, three levels each (fewer where an
 * entry has nothing to view or edit). Levels above `cap` are disabled, and
 * "Zugriff verwalten" only for admins — the API checks the same rules.
 */
function RightsEditor({
  value,
  onChange,
  cap,
  isAdmin,
  disabled = false,
}: {
  value: Rights;
  onChange: (r: Rights) => void;
  cap: Rights;
  isAdmin: boolean;
  disabled?: boolean;
}) {
  const allowedLevels = (key: EntryKey, levels: GrantedLevel[]): Level[] =>
    (['none', ...levels] as Level[]).filter(
      (l) => l === 'none' || ((isAdmin || key !== 'access') && RANK[l] <= RANK[levelOf(cap, key)])
    );
  const set = (key: EntryKey, level: Level) => {
    const next = { ...value };
    if (level === 'none') delete next[key];
    else next[key] = level;
    onChange(next);
  };
  const fill = (mode: 'view' | 'max' | 'none') => {
    const next: Rights = {};
    if (mode !== 'none') {
      for (const e of ENTRIES) {
        const opts = allowedLevels(e.key, e.levels).filter((l) => l !== 'none');
        const pick = mode === 'view' ? opts.find((l) => l === 'view') : opts[opts.length - 1];
        if (pick && e.key !== 'access') next[e.key] = pick;
      }
    }
    onChange(next);
  };

  return (
    <div>
      {!disabled && (
        <div className="mb-2 flex flex-wrap gap-1 text-xs">
          <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => fill('view')}>
            Alles ansehen
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => fill('max')}>
            Alles bearbeiten
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => fill('none')}>
            Alles leeren
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {ENTRY_GROUPS.map((g) => (
          <div key={g.key} className="rounded-md bg-muted/40 p-2">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{g.label}</div>
            {ENTRIES.filter((e) => e.group === g.key).map((e) => {
              const current = levelOf(value, e.key);
              const options = allowedLevels(e.key, e.levels);
              return (
                <label key={e.key} className="flex items-center justify-between gap-2 py-0.5 text-xs">
                  <span className="truncate">{e.label}</span>
                  <select
                    className={cn(
                      'h-7 rounded border border-input bg-background px-1 text-xs',
                      current === 'edit' && 'text-emerald-600 dark:text-emerald-400',
                      current === 'none' && 'text-muted-foreground'
                    )}
                    value={current}
                    disabled={disabled}
                    onChange={(ev) => set(e.key, ev.target.value as Level)}
                  >
                    {(['none', ...e.levels] as Level[]).map((l) => (
                      <option key={l} value={l} disabled={!options.includes(l) && l !== current}>
                        {LEVEL_LABEL[l]}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ bits

function SectionTitle({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground', className)}>{children}</div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  if (!error) return null;
  return <p className="mt-2 text-sm text-destructive">{error instanceof Error ? error.message : 'Fehler'}</p>;
}
